import {
  DataLiabilityBuildersCreator,
  DataLiabilityPetCreator,
  DataLiabilityPrivateCreator,
  DataLiabilityPropertyOwnersCreator,
  DataLiabilitySportBoatCreator,
  DataLiabilityWaterPollutionCreator
} from './SHURDataLiabilityCreator'
import {
  type Contract,
  type GEBLine,
  type HALine,
  type HRLine,
  type Person,
  ProductSubType,
  ProductType,
  type RSLine,
  type TRLine,
  type UNLine
} from 'shur-api'
import { SHUREmptyDataCreator } from './SHUREmptyDataCreator'
import { SHURDataAccidentCreator } from './SHURDataAccidentCreator'
import { SHURDataHouseholdcontentsCreator } from './SHURDataHouseholdcontentsCreator'
import {
  DataLegalDriverCreator,
  DataLegalExtendedCriminalCreator,
  DataLegalFTCreator,
  DataLegalPnPCreator,
  DataLegalPPFTCreator,
  DataLegalPropLandlordCreator,
  DataLegalPropOwnerCreator,
  DataLegalPropTenantCreator,
  DataLegalRoadUserCreator
} from './SHURDataLegalCreator'
import type { DataBlock } from 'de-zurich-ui-library'
import { type DataBlockArrayCreator } from './SHURDataBlockCreator'
import { isTrue } from '@/utils/CommonUtils'
import { DataTransportCostCreator } from '@/utils/shur/SHURDataTransportCreator'
import { SHURDataTransportUnknownCreator } from '@/utils/shur/SHURDataTransportUnknownCreator'
import { SHURDataBuildingCreator } from '@/utils/shur/SHURDataBuildingCreator'

const showBuildingDetails = true

export type lineTypes = HALine | UNLine | HRLine | RSLine | TRLine | GEBLine // Add all lines

export class SHURDataBlockFactory {
  private readonly productSubTypeMap = new Map<ProductSubType, DataBlockArrayCreator>([
    [
      'ACCIDENT',
      isTrue(
        globalThis.ENV_CONFIG?.FEATURE_FLAGS?.SHUR?.ACTIVE_UN_SECTIONS?.ACCIDENT ?? false
      )
        ? new SHURDataAccidentCreator()
        : new SHUREmptyDataCreator()
    ],
    [
      'LIABILITY_PRIVATE',
      isTrue(globalThis.ENV_CONFIG?.FEATURE_FLAGS?.SHUR?.ACTIVE_HA_SECTIONS?.PHV ?? false)
        ? new DataLiabilityPrivateCreator()
        : new SHUREmptyDataCreator()
    ],
    [
      'LIABILITY_PET',
      isTrue(globalThis.ENV_CONFIG?.FEATURE_FLAGS?.SHUR?.ACTIVE_HA_SECTIONS?.THV ?? false)
        ? new DataLiabilityPetCreator()
        : new SHUREmptyDataCreator()
    ],
    [
      'LIABILITY_SPORTS_BOAT',
      isTrue(
        globalThis.ENV_CONFIG?.FEATURE_FLAGS?.SHUR?.ACTIVE_HA_SECTIONS?.SBHV ?? false
      )
        ? new DataLiabilitySportBoatCreator()
        : new SHUREmptyDataCreator()
    ],
    [
      'LIABILITY_PROPERTY_OWNERS',
      isTrue(
        globalThis.ENV_CONFIG?.FEATURE_FLAGS?.SHUR?.ACTIVE_HA_SECTIONS?.HuGHV ?? false
      )
        ? new DataLiabilityPropertyOwnersCreator()
        : new SHUREmptyDataCreator()
    ],
    [
      'LIABILITY_WATER_POLLUTION',
      isTrue(
        globalThis.ENV_CONFIG?.FEATURE_FLAGS?.SHUR?.ACTIVE_HA_SECTIONS?.GSHV ?? false
      )
        ? new DataLiabilityWaterPollutionCreator()
        : new SHUREmptyDataCreator()
    ],
    ['LIABILITY_HUNTING', new SHUREmptyDataCreator()],
    [
      'LIABILITY_BUILDERS',
      isTrue(
        globalThis.ENV_CONFIG?.FEATURE_FLAGS?.SHUR?.ACTIVE_HA_SECTIONS?.BHHV ?? false
      )
        ? new DataLiabilityBuildersCreator()
        : new SHUREmptyDataCreator()
    ],
    [
      'HOUSEHOLDCONTENTS',
      isTrue(
        globalThis.ENV_CONFIG?.FEATURE_FLAGS?.SHUR?.ACTIVE_HR_SECTIONS
          ?.HOUSEHOLDCONTENTS ?? false
      )
        ? new SHURDataHouseholdcontentsCreator()
        : new SHUREmptyDataCreator()
    ],
    ['GLAS', new SHUREmptyDataCreator()],
    [
      'BUILDING',
      isTrue(globalThis.ENV_CONFIG?.FEATURE_FLAGS?.SHUR?.ACTIVE_GEB_SECTIONS ?? false)
        ? new SHURDataBuildingCreator()
        : new SHUREmptyDataCreator()
    ],
    [
      'LEGAL_PRIVATE_AND_PROFESSIONAL',
      isTrue(
        globalThis.ENV_CONFIG?.FEATURE_FLAGS?.SHUR?.ACTIVE_RS_SECTIONS?.LEGAL ?? false
      )
        ? new DataLegalPnPCreator()
        : new SHUREmptyDataCreator()
    ],
    [
      'LEGAL_ROAD_USERS',
      isTrue(
        globalThis.ENV_CONFIG?.FEATURE_FLAGS?.SHUR?.ACTIVE_RS_SECTIONS?.LEGAL ?? false
      )
        ? new DataLegalRoadUserCreator()
        : new SHUREmptyDataCreator()
    ],
    [
      'LEGAL_PROPERTY_LANDLORD',
      isTrue(
        globalThis.ENV_CONFIG?.FEATURE_FLAGS?.SHUR?.ACTIVE_RS_SECTIONS?.LEGAL ?? false
      )
        ? new DataLegalPropLandlordCreator()
        : new SHUREmptyDataCreator()
    ],
    [
      'LEGAL_PROPERTY_OWNER',
      isTrue(
        globalThis.ENV_CONFIG?.FEATURE_FLAGS?.SHUR?.ACTIVE_RS_SECTIONS?.LEGAL ?? false
      )
        ? new DataLegalPropOwnerCreator()
        : new SHUREmptyDataCreator()
    ],
    [
      'LEGAL_PROPERTY_TENANT',
      isTrue(
        globalThis.ENV_CONFIG?.FEATURE_FLAGS?.SHUR?.ACTIVE_RS_SECTIONS?.LEGAL ?? false
      )
        ? new DataLegalPropTenantCreator()
        : new SHUREmptyDataCreator()
    ],
    [
      'LEGAL_DRIVER',
      isTrue(
        globalThis.ENV_CONFIG?.FEATURE_FLAGS?.SHUR?.ACTIVE_RS_SECTIONS?.LEGAL ?? false
      )
        ? new DataLegalDriverCreator()
        : new SHUREmptyDataCreator()
    ],
    [
      'LEGAL_PRIVATE_PROFESSIONAL_FAMILY_TRAFFIC',
      isTrue(
        globalThis.ENV_CONFIG?.FEATURE_FLAGS?.SHUR?.ACTIVE_RS_SECTIONS?.LEGAL ?? false
      )
        ? new DataLegalPPFTCreator()
        : new SHUREmptyDataCreator()
    ],
    [
      'LEGAL_FAMILY_TRAFFIC',
      isTrue(
        globalThis.ENV_CONFIG?.FEATURE_FLAGS?.SHUR?.ACTIVE_RS_SECTIONS?.LEGAL ?? false
      )
        ? new DataLegalFTCreator()
        : new SHUREmptyDataCreator()
    ],
    [
      'LEGAL_EXTENDED_CRIMINAL',
      isTrue(
        globalThis.ENV_CONFIG?.FEATURE_FLAGS?.SHUR?.ACTIVE_RS_SECTIONS?.LEGAL ?? false
      )
        ? new DataLegalExtendedCriminalCreator()
        : new SHUREmptyDataCreator()
    ],
    ['TRAVEL_BAGGAGE', new SHUREmptyDataCreator()],
    ['WEDDING', new SHUREmptyDataCreator()],
    ['TRAVEL_HEALTH', new SHUREmptyDataCreator()],
    [
      'TRAVEL_COST',
      isTrue(
        globalThis.ENV_CONFIG?.FEATURE_FLAGS?.SHUR?.ACTIVE_TR_SECTIONS?.TRAVEL_COST ??
          false
      )
        ? new DataTransportCostCreator()
        : new SHUREmptyDataCreator()
    ]
  ])

  public createDataBlockForLine(line: lineTypes, person?: Person): DataBlock[] {
    if (!line.productSubType) return []

    const dataBlockArrayCreator = this.productSubTypeMap.get(line.productSubType)

    if (!dataBlockArrayCreator) return []

    return dataBlockArrayCreator.createDatablockArray(line, person)
  }

  public createDataBlockForContract(contract: Contract, line: lineTypes): DataBlock[] {
    if (!line.productSubType) return []

    // Special handling for UNKNOWN: only use TransportUnknownCreator for TRANSPORT productType
    if (
      line.productSubType === 'UNKNOWN' &&
      contract.productType === ProductType.Transport
    ) {
      return new SHURDataTransportUnknownCreator().createDatablockArray(contract)
    }

    const dataBlockArrayCreator = this.productSubTypeMap.get(line.productSubType)

    if (!dataBlockArrayCreator) return []

    return dataBlockArrayCreator.createDatablockArray(contract)
  }
}
